/**
 * Reclaim - Saved Immersions Gallery
 * "Your Visions" - A premium personal gallery
 * 
 * NOT a list. NOT a grid of thumbnails.
 * This IS: a personal collection of lived visions
 */

import { useState, useEffect, useMemo, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Play, Eye, Trash2, Plus } from 'lucide-react';
import { useResolvedUserId } from '../../hooks/useResolvedUserId';

// ============================================
// GALLERY CONFIGURATION
// ============================================

const GALLERY_CONFIG = {
  // Card sizes for flowing layout
  sizes: {
    featured: { width: '100%', aspectRatio: '16/10' },
    large: { width: '48%', aspectRatio: '4/3' },
    medium: { width: '31%', aspectRatio: '1/1' },
  },
  // Animation timings
  animation: {
    stagger: 0.08,
    duration: 0.6,
  },
};

// ============================================
// MINI COLLAGE RENDERER
// Renders a scaled-down version of the saved collage
// ============================================

const MiniCollage = ({ images, layoutSeed, size = 'medium' }) => {
  // Seeded random for consistent layout
  const seededRandom = useCallback((index) => {
    const x = Math.sin(layoutSeed * 1000 + index) * 10000;
    return x - Math.floor(x);
  }, [layoutSeed]);

  // Generate mini collage positions — every UNIQUE image shown exactly once.
  const collageItems = useMemo(() => {
    // Dedupe by URL
    const seen = new Set();
    const uniques = [];
    for (const img of images || []) {
      if (!img?.url || seen.has(img.url)) continue;
      seen.add(img.url);
      uniques.push(img);
    }
    if (uniques.length === 0) return [];

    const items = [];
    const total = uniques.length;
    // Tile size scales inversely with count so every image is visible.
    const baseW = total <= 4 ? 42 : total <= 8 ? 32 : total <= 14 ? 24 : total <= 22 ? 18 : 14;

    uniques.forEach((image, index) => {
      let x, y, width, rotation, zIndex;

      if (index === 0) {
        // Hero — centred dominant
        x = 30 + seededRandom(1) * 12;
        y = 24 + seededRandom(2) * 8;
        width = baseW * 1.25;
        zIndex = 30;
      } else if (index < 4) {
        // Anchor positions around the hero
        const positions = [
          { x: -4, y: 6 },
          { x: 65, y: -2 },
          { x: 60, y: 55 },
        ];
        const pos = positions[(index - 1) % 3];
        x = pos.x + seededRandom(10 + index) * 8;
        y = pos.y + seededRandom(20 + index) * 8;
        width = baseW * 1.05;
        zIndex = 20 - index;
      } else {
        // Spread the rest on a seeded grid jitter so EVERY image lands somewhere.
        const cols = total > 16 ? 5 : 4;
        const rows = Math.ceil((total - 4) / cols);
        const gi = index - 4;
        const col = gi % cols;
        const row = Math.floor(gi / cols);
        const cellW = 100 / cols;
        const cellH = 80 / Math.max(rows, 1);
        x = col * cellW - 6 + seededRandom(40 + index) * cellW * 0.35;
        y = 8 + row * cellH + seededRandom(50 + index) * cellH * 0.3;
        width = baseW * (0.85 + seededRandom(60 + index) * 0.25);
        zIndex = 5 + (index % 6);
      }

      rotation = (seededRandom(70 + index) - 0.5) * 10;

      items.push({
        url: image.url,
        style: {
          left: `${x}%`,
          top: `${y}%`,
          width: `${width}%`,
          transform: `rotate(${rotation}deg)`,
          zIndex,
        },
      });
    });

    return items;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [images, seededRandom, size]);

  return (
    <div className="absolute inset-0 overflow-hidden">
      {/* Warm atmospheric background */}
      <div 
        className="absolute inset-0"
        style={{
          background: 'linear-gradient(160deg, #1a2823 0%, #152220 40%, #101c18 80%, #0c1512 100%)',
        }}
      />
      
      {/* Central light bloom */}
      <div 
        className="absolute inset-0"
        style={{
          background: 'radial-gradient(ellipse 120% 100% at 45% 40%, rgba(255, 248, 235, 0.08) 0%, transparent 60%)',
        }}
      />

      {/* Collage images */}
      {collageItems.map((item, index) => (
        <div
          key={index}
          className="absolute rounded-lg overflow-hidden"
          style={{
            ...item.style,
            aspectRatio: '1',
            boxShadow: '0 8px 25px rgba(0,0,0,0.4)',
          }}
        >
          <img 
            src={item.url} 
            alt="" 
            className="w-full h-full object-cover"
            loading="lazy"
          />
        </div>
      ))}

      {/* Soft vignette overlay */}
      <div 
        className="absolute inset-0 pointer-events-none"
        style={{
          background: 'radial-gradient(ellipse at center, transparent 40%, rgba(10, 18, 15, 0.4) 100%)',
        }}
      />
    </div>
  );
};

// ============================================
// IMMERSION CARD
// ============================================

const ImmersionCard = ({ 
  immersion, 
  size = 'medium', 
  onEnterImmersion, 
  onViewBoard,
  onDelete,
  index 
}) => {
  return (
    <motion.div
      className="relative w-full overflow-hidden cursor-pointer"
      style={{
        borderRadius: '16px',
        border: '1px solid rgba(255, 255, 255, 0.06)',
        background: 'rgba(255, 255, 255, 0.03)',
      }}
      initial={{ opacity: 0, y: 20, scale: 0.97 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ 
        delay: index * GALLERY_CONFIG.animation.stagger,
        duration: GALLERY_CONFIG.animation.duration,
        ease: [0.25, 0.1, 0.25, 1],
      }}
      whileHover={{ scale: 1.01 }}
      data-testid={`immersion-card-${index}`}
    >
      <div className="flex items-stretch">
        {/* Mini collage thumbnail */}
        <div className="relative w-28 h-28 md:w-36 md:h-36 flex-shrink-0 overflow-hidden" style={{ borderRadius: '16px 0 0 16px' }}>
          <MiniCollage 
            images={immersion.selected_images || []} 
            layoutSeed={immersion.layout_seed || 0.5}
            size="medium"
          />
        </div>

        {/* Info + actions */}
        <div className="flex-1 flex flex-col justify-center px-5 py-4">
          <h3
            className="text-white/85 text-base md:text-lg mb-1"
            style={{ fontFamily: "'Playfair Display', serif", fontStyle: 'italic' }}
          >
            {immersion.name || 'My Vision'}
          </h3>
          
          <p className="text-white/25 text-xs mb-4" style={{ fontFamily: "'Manrope', sans-serif" }}>
            {immersion.created_at ? new Date(immersion.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : ''}
            {immersion.use_count > 0 && ` · Returned ${immersion.use_count} ${immersion.use_count === 1 ? 'time' : 'times'}`}
          </p>

          <div className="flex gap-3">
            <motion.button
              onClick={(e) => { e.stopPropagation(); onEnterImmersion(immersion); }}
              className="flex items-center gap-2 px-4 py-2 rounded-full text-xs text-white/80"
              style={{
                fontFamily: "'Manrope', sans-serif",
                background: 'rgba(255, 255, 255, 0.08)',
                border: '1px solid rgba(255, 255, 255, 0.12)',
                backdropFilter: 'blur(8px)',
              }}
              whileHover={{ background: 'rgba(255, 255, 255, 0.14)' }}
              whileTap={{ scale: 0.96 }}
              data-testid={`enter-immersion-${index}`}
            >
              <Play size={12} /> Re-enter
            </motion.button>

            <motion.button
              onClick={(e) => { e.stopPropagation(); onViewBoard(immersion); }}
              className="flex items-center gap-2 px-4 py-2 rounded-full text-xs text-white/45"
              style={{
                fontFamily: "'Manrope', sans-serif",
                background: 'rgba(255, 255, 255, 0.03)',
                border: '1px solid rgba(255, 255, 255, 0.06)',
              }}
              whileHover={{ background: 'rgba(255, 255, 255, 0.08)' }}
              whileTap={{ scale: 0.96 }}
              data-testid={`view-board-${index}`}
            >
              <Eye size={12} /> View board
            </motion.button>

            {onDelete && (
              <motion.button
                onClick={(e) => { e.stopPropagation(); onDelete(immersion); }}
                className="flex items-center justify-center p-2 rounded-full text-white/20 hover:text-red-300/60"
                style={{
                  background: 'rgba(255, 255, 255, 0.02)',
                  border: '1px solid rgba(255, 255, 255, 0.04)',
                }}
                whileHover={{ background: 'rgba(255, 80, 80, 0.1)' }}
                whileTap={{ scale: 0.9 }}
                data-testid={`delete-vision-${index}`}
              >
                <Trash2 size={12} />
              </motion.button>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
};

// ============================================
// EMPTY STATE
// ============================================

const EmptyState = ({ onCreateNew }) => (
  <motion.div 
    className="flex flex-col items-center justify-center text-center py-20"
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.6 }}
  >
    <div 
      className="w-24 h-24 rounded-full mb-8 flex items-center justify-center"
      style={{
        background: 'linear-gradient(135deg, rgba(90, 120, 80, 0.2) 0%, rgba(65, 95, 55, 0.1) 100%)',
        border: '1px solid rgba(140, 180, 120, 0.2)',
      }}
    >
      <Plus size={32} className="text-[#4A6741]/60" />
    </div>
    
    <h2 
      className="text-2xl text-white/80 mb-4"
      style={{ fontFamily: "'Playfair Display', serif" }}
    >
      Create your first vision
    </h2>
    
    <p 
      className="text-white/40 mb-8 max-w-xs"
      style={{ fontFamily: "'Manrope', sans-serif" }}
    >
      and return to it daily
    </p>
    
    <motion.button
      onClick={onCreateNew}
      className="px-8 py-4 rounded-full text-base font-medium text-[#FAF9F6]"
      style={{
        fontFamily: "'Manrope', sans-serif",
        background: 'linear-gradient(135deg, rgba(90, 120, 80, 0.9) 0%, rgba(65, 95, 55, 0.95) 100%)',
        boxShadow: '0 0 40px rgba(90, 130, 80, 0.35)',
        border: '1px solid rgba(140, 180, 120, 0.3)',
      }}
      whileHover={{ scale: 1.03 }}
      whileTap={{ scale: 0.97 }}
      data-testid="create-first-vision-btn"
    >
      Begin
    </motion.button>
  </motion.div>
);

// ============================================
// MAIN GALLERY COMPONENT
// ============================================

const SavedImmersionsGallery = () => {
  const navigate = useNavigate();
  const [immersions, setImmersions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Resolve userId — authenticated email when logged in, anon fallback otherwise.
  const userId = useResolvedUserId();

  // Load saved immersions
  useEffect(() => {
    const loadImmersions = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/vision-board/immersions/${userId}`
        );
        if (response.ok) {
          const data = await response.json();
          setImmersions(data.immersions || []);
        } else {
          setError('Failed to load your visions');
        }
      } catch (err) {
        console.error('Failed to load immersions:', err);
        setError('Failed to load your visions');
      } finally {
        setLoading(false);
      }
    };

    loadImmersions();
  }, [userId]);

  // Handlers
  const handleEnterImmersion = async (immersion) => {
    // Record usage
    try {
      await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/vision-board/immersions/${userId}/${immersion.id}/use`,
        { method: 'POST' }
      );
    } catch (err) {
      console.error('Failed to record usage:', err);
    }
    
    // Navigate to immersion with saved data
    navigate('/abundance/living-vision-board', { 
      state: { 
        savedImmersion: immersion,
        startAtStep: 6 // Jump to immersion
      } 
    });
  };

  const handleViewBoard = (immersion) => {
    navigate('/abundance/living-vision-board', { 
      state: { 
        savedImmersion: immersion,
        startAtStep: 4 // Jump to reveal
      } 
    });
  };

  const handleCreateNew = () => {
    navigate('/abundance/living-vision-board');
  };

  const handleDelete = async (immersion) => {
    if (!window.confirm(`Release "${immersion.name || 'My Vision'}"? This cannot be undone.`)) return;
    try {
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/vision-board/immersions/${userId}/${immersion.id}`,
        { method: 'DELETE' }
      );
      if (res.ok) {
        setImmersions(prev => prev.filter(i => i.id !== immersion.id));
      }
    } catch (err) {
      console.error('Failed to delete:', err);
    }
  };

  const handleBack = () => {
    navigate('/map');
  };

  // Determine card sizes based on position
  const getCardSize = (index, total) => {
    if (index === 0 && total > 0) return 'featured';
    if (index < 3) return 'large';
    return 'medium';
  };

  return (
    <div className="min-h-screen bg-[#0a0c10]">
      {/* Atmospheric background */}
      <div 
        className="fixed inset-0 pointer-events-none"
        style={{
          background: `
            radial-gradient(ellipse 100% 100% at 20% 10%, rgba(74, 103, 65, 0.08) 0%, transparent 50%),
            radial-gradient(ellipse 80% 80% at 80% 90%, rgba(90, 120, 80, 0.06) 0%, transparent 45%),
            linear-gradient(to bottom, #0a0c10 0%, #0d1012 50%, #0a0c10 100%)
          `,
        }}
      />

      {/* Header */}
      <motion.header 
        className="sticky top-0 z-40 px-6 py-5 flex items-center justify-between"
        style={{
          background: 'linear-gradient(to bottom, rgba(10, 12, 16, 0.95) 0%, rgba(10, 12, 16, 0) 100%)',
          backdropFilter: 'blur(12px)',
        }}
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <button
          onClick={handleBack}
          className="p-2.5 rounded-full bg-white/5 hover:bg-white/10 transition-all opacity-0 pointer-events-none"
          style={{ border: '1px solid rgba(255, 255, 255, 0.06)' }}
          aria-hidden="true"
          tabIndex={-1}
        >
          <ArrowLeft size={18} className="text-white/50" />
        </button>

        <div className="text-center">
          <h1 
            className="text-xl text-white/90"
            style={{ fontFamily: "'Playfair Display', serif", fontStyle: 'italic' }}
          >
            Your Visions
          </h1>
          <p 
            className="text-white/30 text-xs mt-1"
            style={{ fontFamily: "'Manrope', sans-serif" }}
          >
            Return. Let it deepen.
          </p>
        </div>

        <motion.button
          onClick={handleCreateNew}
          className="p-2.5 rounded-full bg-[#4A6741]/30 hover:bg-[#4A6741]/50 transition-all"
          style={{ border: '1px solid rgba(74, 103, 65, 0.3)' }}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          data-testid="create-new-btn"
        >
          <Plus size={18} className="text-[#4A6741]" />
        </motion.button>
      </motion.header>

      {/* Content */}
      <main className="relative z-10 px-6 pb-20">
        {loading ? (
          // Loading state
          <div className="flex items-center justify-center py-32">
            <motion.div
              className="w-12 h-12 rounded-full border-2 border-[#4A6741]/30 border-t-[#4A6741]"
              animate={{ rotate: 360 }}
              transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
            />
          </div>
        ) : error ? (
          // Error state
          <div className="text-center py-20">
            <p className="text-white/40">{error}</p>
            <button
              onClick={() => window.location.reload()}
              className="mt-4 text-[#4A6741] underline"
            >
              Try again
            </button>
          </div>
        ) : immersions.length === 0 ? (
          // Empty state
          <EmptyState onCreateNew={handleCreateNew} />
        ) : (
          // Vertical list — thumbnails, names, re-enter
          <div className="flex flex-col gap-4 max-w-xl mx-auto w-full">
            {immersions.map((immersion, index) => (
              <ImmersionCard
                key={immersion.id}
                immersion={immersion}
                onEnterImmersion={handleEnterImmersion}
                onViewBoard={handleViewBoard}
                onDelete={handleDelete}
                index={index}
              />
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default SavedImmersionsGallery;
